export const USER_ROLES = ["admin", "admin_support", "broker", "underwriter"] as const;
export type UserRoles = (typeof USER_ROLES)[number];

export const ROLE_LABELS: Record<UserRoles, string> = {
  admin: "Administrator",
  admin_support: "Admin Support",
  broker: "Broker",
  underwriter: "Underwriter",
};

export interface UserOrganization {
  id: number;
  name: string;
}

/** An organization the signed-in user has an active membership in. */
export interface UserMembership extends UserOrganization {
  joined_at?: string | null;
  /** The organization `organization_name` (the portal name) comes from. */
  is_primary?: boolean;
}

export interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  /** Display name: first+last when set, otherwise email. */
  name?: string;
  role: UserRoles;
  company_name?: string;
  organization_name?: string;
  organizations?: UserMembership[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /** ISO timestamp of last successful login, when available. */
  last_login?: string | null;
  /** Pending login email awaiting confirmation link, if any. */
  pending_email?: string | null;
  /** Server-decided: this user's organization is a trial account (locked settings, no inbox). */
  is_trial_account?: boolean;
  /** Where merchant-bound mail (contracts, Plaid links) is redirected for trial accounts. */
  trial_recipient_email?: string;
  /** System admin: manages the system templates every organization shares. */
  is_superuser?: boolean;
  /** Django-admin toggle: org managers may open Billing for this organization. */
  billing_enabled?: boolean;
  /** Server-decided feature access (backend ``build_user_capabilities``). */
  capabilities?: UserCapabilities;
  /** SANDBOX_DEV_INSPECTOR: the sandbox's dev inspector is on (local development only). */
  sandbox_dev_inspector?: boolean;
}

export interface UserCapabilities {
  /** Staff user whose organization has the AI agent on and whose admins gave them AI access. */
  agent: boolean;
  /** Admin of an organization with the AI agent on: chooses which of its users get AI access. */
  manage_agent_access?: boolean;
}

export type AccountType = "standard" | "trial";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  /** True after the initial cookie session check (/me) finishes. */
  sessionChecked: boolean;
  loading: boolean;
  error: string | null;
  activeOrganizationId: number | null;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface AdminRegisterCredentials {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
  company_name: string;
  invite_code: string;
  /** Defaults to "standard" server-side when omitted. */
  account_type?: AccountType;
}
